import React, { useState } from 'react';
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Stack,
  Collapse,
  Button
} from '@mui/material';
import {
  Edit,
  Delete,
  Warning,
  CheckCircle,
  ExpandMore,
  ExpandLess,
  Lock,
  Sync,
  PictureAsPdf,
  Download
} from '@mui/icons-material';
import { formatDateTime, formatDuration, getStatusColor, getDutyAircraftInfo } from '../utils/crewDutyRosterUtils';
import RosterFlightDutyChip from './RosterFlightDutyChip';
import CrewClearanceChips from './CrewClearanceChips';
import { isCrewRosterHighlightCleared } from '../../../../utils/crewClearance';
import { formatDateTimeWithTimezone } from '../../../../utils/timezoneUtils';
import {
  downloadAuthenticatedPdf,
  viewAuthenticatedPdf
} from '../../../../utils/authenticatedPdf';
import { useToast } from '../../../../contexts/ToastContext';
import dayjs from 'dayjs';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const proposedDiffers = (entry) => {
  if (!entry?.proposedDutyStartTime || !entry?.proposedDutyEndTime) return false;
  const a = entry.dutyStartTime ? new Date(entry.dutyStartTime).getTime() : null;
  const b = entry.dutyEndTime ? new Date(entry.dutyEndTime).getTime() : null;
  const pA = new Date(entry.proposedDutyStartTime).getTime();
  const pB = new Date(entry.proposedDutyEndTime).getTime();
  return a !== pA || b !== pB;
};

const formatLegRoute = (leg) => {
  const dep = (leg.departurePlace || '').trim();
  const arr = (leg.arrivalPlace || '').trim();
  if (dep && arr) return `${dep}–${arr}`;
  if (dep || arr) return dep || arr;
  return `S${leg.sectorSlot || leg.sectorNumber || '?'}`;
};

/**
 * Table View component for Crew Duty Roster
 */
const CrewDutyRosterTable = ({
  entries,
  loading,
  availableCrew,
  crewMembers,
  filters,
  canEdit,
  canDelete,
  handleEdit,
  handleDelete,
  handleApplyTechlogProposed,
  displayTimezone
}) => {
  const [expandedId, setExpandedId] = useState(null);
  const { showToast } = useToast();
  const [pdfBusyId, setPdfBusyId] = useState(null);
  const formatDutyTime = (date) =>
    displayTimezone ? formatDateTimeWithTimezone(date, null, displayTimezone) : formatDateTime(date);

  const openDutyPdf = async (entry, download = false) => {
    if (!entry?._id) return;
    setPdfBusyId(entry._id);
    try {
      const url = `${API_URL}/ops/crew-duty-roster/${entry._id}/pdf${download ? '?download=1' : ''}`;
      const crewName = entry.crewId
        ? `${entry.crewId.firstName || ''}_${entry.crewId.lastName || ''}`.trim()
        : 'crew';
      const day = entry.dutyStartTime
        ? new Date(entry.dutyStartTime).toISOString().slice(0, 10)
        : 'duty';
      const name = `duty-assignment-${crewName}-${day}.pdf`.replace(/\s+/g, '_');
      if (download) await downloadAuthenticatedPdf(url, name);
      else await viewAuthenticatedPdf(url);
    } catch (error) {
      showToast(
        typeof error?.message === 'string' && !error.message.startsWith('{')
          ? error.message
          : 'Failed to open duty PDF',
        'error'
      );
    } finally {
      setPdfBusyId(null);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={8}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={36} />
                <TableCell>Crew Member</TableCell>
                <TableCell>Aircraft Type</TableCell>
                <TableCell>Duty Type</TableCell>
                <TableCell>Training</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>End Time</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Compliance</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">Loading...</TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">No duty roster entries found</TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => {
                  const legs = Array.isArray(entry.sectorLegs) ? entry.sectorLegs : [];
                  const hasLegs = legs.length > 0;
                  const showApply =
                    canEdit(entry) &&
                    typeof handleApplyTechlogProposed === 'function' &&
                    entry.proposedDutyStartTime &&
                    entry.proposedDutyEndTime &&
                    (entry.manualTimeLock || proposedDiffers(entry));
                  const expanded = expandedId === entry._id;
                  const { label: aircraftLabel } = getDutyAircraftInfo(entry);

                  return (
                    <React.Fragment key={entry._id}>
                      <TableRow
                        onClick={() => canEdit(entry) && handleEdit(entry)}
                        sx={{
                          cursor: canEdit(entry) ? 'pointer' : 'default',
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()} sx={{ px: 0.5 }}>
                          {hasLegs && (
                            <IconButton
                              size="small"
                              aria-label={expanded ? 'Hide sectors' : 'Show sectors'}
                              onClick={() => setExpandedId(expanded ? null : entry._id)}
                            >
                              {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                            <span>
                              {entry.crewId?.firstName} {entry.crewId?.lastName}
                            </span>
                            <CrewClearanceChips crew={entry.crewId} />
                            {entry.manualTimeLock && (
                              <Tooltip title="Times manually edited — techlog sync updates proposed only">
                                <Chip icon={<Lock />} label="Locked" size="small" variant="outlined" />
                              </Tooltip>
                            )}
                            {entry.timeSource === 'TECHLOG_SYNC' && !entry.manualTimeLock && (
                              <Tooltip title="Times driven by techlog sync">
                                <Chip icon={<Sync />} label="Techlog" size="small" color="info" variant="outlined" />
                              </Tooltip>
                            )}
                          </Stack>
                          {hasLegs && (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                              {legs.map((leg, idx) => (
                                <Chip
                                  key={`${entry._id}-leg-${idx}`}
                                  size="small"
                                  variant="outlined"
                                  label={`S${leg.sectorSlot || leg.sectorNumber || idx + 1} ${formatLegRoute(leg)}`}
                                  sx={{ height: 20, fontSize: '0.65rem' }}
                                />
                              ))}
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Chip
                              label={entry.aircraftType || 'Helicopter'}
                              color={entry.aircraftType === 'Fixed Wing' ? 'primary' : 'default'}
                              size="small"
                              variant="outlined"
                            />
                            {aircraftLabel ? (
                              <Typography component="span" variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                                {aircraftLabel}
                              </Typography>
                            ) : null}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {entry.dutyType === 'FLIGHT' ? (
                            <Box>
                              <RosterFlightDutyChip
                                entry={entry}
                                highlightCleared={isCrewRosterHighlightCleared(entry.crewId)}
                                sx={{ maxWidth: 'none' }}
                              />
                              {entry.rosterPlanOnly ? (
                                <Typography component="span" variant="caption" display="block" color="text.secondary">
                                  (plan)
                                </Typography>
                              ) : null}
                              {(entry.tripId?.charterNumber || entry.linkedToFlight) && (
                                <Typography component="span" variant="caption" display="block" color="text.secondary">
                                  {entry.tripId?.charterNumber
                                    ? `Linked: ${entry.tripId.charterNumber}`
                                    : 'Linked to charter'}
                                </Typography>
                              )}
                            </Box>
                          ) : entry.dutyType === 'SCHEDULED_DUTY_OFF' ? (
                            entry.autoGenerated ? (
                              <Box>
                                <RosterFlightDutyChip entry={entry} sx={{ maxWidth: 'none' }} />
                              </Box>
                            ) : (
                              'Scheduled off'
                            )
                          ) : (
                            entry.dutyType
                          )}
                        </TableCell>
                        <TableCell>
                          {entry.dutyType === 'TRAINING' ? (
                            <>
                              {entry.trainingCategory === 'FLIGHT_TRAINING' ? 'Flight Training' : 'Ground Training'}
                              {entry.trainingCategory === 'FLIGHT_TRAINING' && entry.trainingAircraftId && (
                                <Typography component="span" variant="caption" display="block" color="text.secondary">
                                  {entry.trainingAircraftId?.registration || entry.trainingAircraftId}
                                </Typography>
                              )}
                            </>
                          ) : '—'}
                        </TableCell>
                        <TableCell>{formatDutyTime(entry.dutyStartTime)}</TableCell>
                        <TableCell>{formatDutyTime(entry.dutyEndTime)}</TableCell>
                        <TableCell>{formatDuration(entry.dutyDuration)}</TableCell>
                        <TableCell>
                          <Chip
                            label={entry.status}
                            color={getStatusColor(entry.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {entry.isNonCompliant ? (
                            <Tooltip
                              title={
                                <Box>
                                  <Typography variant="caption" display="block">
                                    Aircraft Type: {entry.aircraftType || 'Helicopter'}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    {entry.nonComplianceJustification || 'Non-compliant'}
                                  </Typography>
                                </Box>
                              }
                            >
                              <Chip
                                icon={<Warning />}
                                label="Non-Compliant"
                                color="error"
                                size="small"
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title={`Aircraft Type: ${entry.aircraftType || 'Helicopter'}`}>
                              <Chip
                                icon={<CheckCircle />}
                                label="Compliant"
                                color="success"
                                size="small"
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Tooltip title="View PDF (share with pilot)">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={pdfBusyId === entry._id}
                                  onClick={() => openDutyPdf(entry, false)}
                                  aria-label="View duty PDF"
                                >
                                  <PictureAsPdf fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Download PDF">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={pdfBusyId === entry._id}
                                  onClick={() => openDutyPdf(entry, true)}
                                  aria-label="Download duty PDF"
                                >
                                  <Download fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {canEdit(entry) && (
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => handleEdit(entry)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canDelete(entry) && entry.status !== 'CANCELLED' && (
                              <Tooltip title="Cancel">
                                <IconButton size="small" onClick={() => handleDelete(entry)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {showApply && (
                              <Tooltip
                                title={`Techlog proposes ${formatDutyTime(entry.proposedDutyStartTime)} → ${formatDutyTime(entry.proposedDutyEndTime)}`}
                              >
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleApplyTechlogProposed(entry)}
                                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                                >
                                  Apply techlog
                                </Button>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                      {hasLegs && (
                        <TableRow>
                          <TableCell colSpan={11} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 1.5, px: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Sector legs (from techlog)
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Slot</TableCell>
                                      <TableCell>Route</TableCell>
                                      <TableCell>Rotor start</TableCell>
                                      <TableCell>Rotor stop</TableCell>
                                      <TableCell>Leg start</TableCell>
                                      <TableCell>Leg end</TableCell>
                                      <TableCell>Block</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {legs.map((leg, idx) => (
                                      <TableRow key={`${entry._id}-detail-${idx}`}>
                                        <TableCell>S{leg.sectorSlot || leg.sectorNumber || idx + 1}</TableCell>
                                        <TableCell>{formatLegRoute(leg)}</TableCell>
                                        <TableCell>{leg.rotorStartTime ? formatDutyTime(leg.rotorStartTime) : '—'}</TableCell>
                                        <TableCell>{leg.rotorStopTime ? formatDutyTime(leg.rotorStopTime) : '—'}</TableCell>
                                        <TableCell>{leg.computedLegStart ? formatDutyTime(leg.computedLegStart) : '—'}</TableCell>
                                        <TableCell>{leg.computedLegEnd ? formatDutyTime(leg.computedLegEnd) : '—'}</TableCell>
                                        <TableCell>
                                          {leg.blockTimeHours != null
                                            ? `${Number(leg.blockTimeHours).toFixed(2)}h`
                                            : '—'}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                                {entry.lastTechlogSyncAt && (
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                    Last techlog sync: {formatDutyTime(entry.lastTechlogSyncAt)}
                                    {entry.proposedDutyStartTime && entry.proposedDutyEndTime
                                      ? ` · Proposed duty ${formatDutyTime(entry.proposedDutyStartTime)} → ${formatDutyTime(entry.proposedDutyEndTime)}`
                                      : ''}
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Available Crew (by filters)
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Showing crew with availability status <strong>AVAILABLE</strong>
              {filters.startDate && filters.endDate && (
                <> between <strong>{filters.startDate}</strong> and <strong>{filters.endDate}</strong></>
              )}
              {!filters.startDate && !filters.endDate && ' for the selected criteria.'}
            </Typography>
            <Divider sx={{ my: 1 }} />
            {availableCrew.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No available crew found for the current filters.
              </Typography>
            ) : (
              <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
                {availableCrew.map((av) => (
                  <Box
                    key={av._id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 0.5
                    }}
                  >
                    <Box>
                      <Typography variant="body2">
                        {av.crewId?.firstName} {av.crewId?.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {av.availabilityStatus} | {dayjs(av.startDate).format('DD MMM YYYY HH:mm')} - {dayjs(av.endDate).format('DD MMM YYYY HH:mm')}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default CrewDutyRosterTable;
